import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useJournalStore, Journal, SharedJournal } from '../../../stores/journalStore';
import { BoardJournalList } from './BoardJournalList';
import { BoardJournalDetails } from './BoardJournalDetails';
import { EditJournalNameModal } from './modals/EditJournalNameModal';

import { ShareJournalModal } from './modals/ShareJournalModal';
import { SectionLoader } from '../../../components/LogoLoader';
import { usePlanFeaturesStore } from '../../../stores/planFeaturesStore';
import { UpgradeModal } from '../../subscription/UpgradeModal';

type TabType = 'my' | 'shared';

const MoneyJournalView = ({
    autoOpenAdd = false,
    onAddConsumed,
    onAddComplete,
    returnToBoard,
    onReturnToBoard,
    onMenuClick
}: {
    autoOpenAdd?: boolean;
    onAddConsumed?: () => void;
    onAddComplete?: () => void;
    returnToBoard?: { id: string; name: string } | null;
    onReturnToBoard?: () => void;
    onMenuClick?: () => void;
}) => {
    const { t } = useTranslation('common');
    const {
        journals,
        sharedJournals,
        loadJournals,
        loadSharedJournals,
        isLoading,
        isLoadingShared,
        deleteJournal,
        removeSharedAccess,
        addJournal
    } = useJournalStore();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<TabType>(() => {
        return searchParams.get('journalTab') === 'shared' ? 'shared' : 'my';
    });
    const [selectedJournal, setSelectedJournal] = useState<Journal | SharedJournal | null>(() => {
        const id = searchParams.get('journalId');
        if (id && journals.length > 0) {
            return journals.find(j => j.id === id) || null;
        }
        return null;
    });

    // Share modal state
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [journalToShare, setJournalToShare] = useState<{ id: string; name: string } | null>(null);

    // Rename modal state
    const [renamingJournal, setRenamingJournal] = useState<Journal | null>(null);

    // Upgrade modal state
    const [upgradeModal, setUpgradeModal] = useState<{ isOpen: boolean; featureKey: string; currentUsage?: number }>({ isOpen: false, featureKey: '' });
    const { getFeatureValue } = usePlanFeaturesStore();

    // Initialize synced state: true if no ID or if ID matches available data immediately
    const [isUrlSynced, setIsUrlSynced] = useState(() => {
        const id = searchParams.get('journalId');
        if (!id) return true;
        if (journals.length > 0 && journals.find(j => j.id === id)) return true;
        // If ID exists but data not ready, wait
        return false;
    });
    const [hasMounted, setHasMounted] = useState(false);
    const searchParamsRef = useRef(searchParams);
    const autoOpenHandledRef = useRef(false);
    const isCreatingRef = useRef(false);
    const onAddConsumedRef = useRef(onAddConsumed);

    useEffect(() => {
        searchParamsRef.current = searchParams;
    }, [searchParams]);

    useEffect(() => {
        onAddConsumedRef.current = onAddConsumed;
    }, [onAddConsumed]);

    useEffect(() => {
        loadJournals();
        loadSharedJournals();
        setHasMounted(true);
    }, [loadJournals, loadSharedJournals]);

    // url sync: read
    useEffect(() => {
        if (!hasMounted) return;

        const journalId = searchParams.get('journalId');
        const allJournals = [...journals, ...sharedJournals];

        if (journalId) {
            if (allJournals.length > 0) {
                const journal = allJournals.find(j => j.id === journalId);
                setSelectedJournal(journal || null);
                setIsUrlSynced(true);
            } else if (!isLoading && !isLoadingShared) {
                setSelectedJournal(null);
                setIsUrlSynced(true);
            }
        } else {
            setSelectedJournal(null);
            setIsUrlSynced(true);
        }
    }, [searchParams, journals, sharedJournals, isLoading, isLoadingShared, hasMounted]);

    const handleCreateJournal = async () => {
        // Prevent concurrent creation calls (guards against double-execution from
        // React StrictMode, navigate-triggered re-renders, or timing issues)
        if (isCreatingRef.current) return;
        isCreatingRef.current = true;

        const baseName = "Journal";
        let counter = 1;
        const usedNames = new Set(journals.map(j => j.name));
        while (usedNames.has(`${baseName} ${counter}`)) {
            counter++;
        }
        const newName = `${baseName} ${counter}`;

        // Plan Feature Check: Max Journals
        const maxJournals = getFeatureValue('max_journals');
        if (maxJournals !== -1 && journals.length >= maxJournals) {
            setUpgradeModal({
                isOpen: true,
                featureKey: 'max_journals',
                currentUsage: journals.length
            });
            isCreatingRef.current = false;
            return;
        }

        try {
            const newJournal = await addJournal(newName);
            if (newJournal) {
                handleJournalSelect(newJournal);
                setRenamingJournal(newJournal);
                if (onAddComplete) onAddComplete();
            }
        } catch (error) {
            console.error('Failed to create journal', error);
        } finally {
            isCreatingRef.current = false;
        }
    };

    useEffect(() => {
        if (autoOpenAdd && !autoOpenHandledRef.current) {
            autoOpenHandledRef.current = true;
            // Signal the parent to consume the flag FIRST to prevent re-triggers
            if (onAddConsumedRef.current) onAddConsumedRef.current();
            // Then perform the creation
            handleCreateJournal();
        }
    }, [autoOpenAdd]);

    const handleTabChange = (tab: TabType) => {
        setActiveTab(tab);
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            if (tab === 'shared') {
                newParams.set('journalTab', 'shared');
            } else {
                newParams.delete('journalTab');
            }
            newParams.delete('journalId');
            return newParams;
        });
        setSelectedJournal(null);
    };

    const handleJournalSelect = (journal: Journal | SharedJournal | null) => {
        if (journal) {
            setSearchParams(prev => {
                const newParams = new URLSearchParams(prev);
                newParams.set('journalId', journal.id);
                // Ensure action and return info are never accidentally preserved in race conditions
                newParams.delete('action');
                newParams.delete('returnToBoardId');
                newParams.delete('returnToBoardName');
                return newParams;
            });
        } else {
            setSearchParams(prev => {
                const newParams = new URLSearchParams(prev);
                newParams.delete('journalId');
                newParams.delete('action');
                newParams.delete('returnToBoardId');
                newParams.delete('returnToBoardName');
                return newParams;
            });
        }
    };

    const handleShareJournal = (journal: Journal) => {
        setJournalToShare({ id: journal.id, name: journal.name });
        setShareModalOpen(true);
    };

    const currentJournals = activeTab === 'my' ? journals : sharedJournals;
    const currentLoading = activeTab === 'my' ? isLoading : isLoadingShared;

    // Check if selected journal is a shared one (to determine permissions)
    const isSharedJournal = selectedJournal && 'owner' in selectedJournal;
    const sharedPermission = isSharedJournal ? (selectedJournal as SharedJournal).permission : null;

    const hasJournalInUrl = !!searchParams.get('journalId');
    const isShowingDetails = !!selectedJournal || (hasJournalInUrl && !isUrlSynced);

    return (
        <div className="h-full flex flex-col bg-background-page dark:bg-neutral-950">

            {/* Main Content Area with Page Transition */}
            <AnimatePresence mode="wait" initial={false}>
                {isShowingDetails ? (
                    <motion.div
                        key="details"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                        className="w-full h-full relative"
                    >
                        {!selectedJournal ? (
                            <div className="flex items-center justify-center h-full">
                                <SectionLoader size="sm" />
                            </div>
                        ) : (
                            <BoardJournalDetails
                                boardId=""
                                selectedJournal={selectedJournal as any}
                                onBack={() => handleJournalSelect(null)}
                                standalone
                                readOnly={sharedPermission === 'read'}
                                sharedBy={isSharedJournal ? (selectedJournal as SharedJournal).owner.name : undefined}
                                returnToBoard={returnToBoard}
                                onReturnToBoard={onReturnToBoard}
                                onRename={!isSharedJournal ? () => setRenamingJournal(selectedJournal as Journal) : undefined}
                                onMenuClick={onMenuClick}
                            />
                        )}
                    </motion.div>
                ) : (
                    <motion.div
                        key="list-view"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                        className="w-full h-full flex flex-col relative"
                    >
                        {/* Unified Header Structure - Matching ConnectedChannels and BoardExplorer */}
                        <div className="flex items-center justify-between mb-4 pt-4 lg:pt-0 lg:mb-6 shrink-0">
                            <div className="flex items-center gap-3">
                                {onMenuClick && (
                                    <button
                                        onClick={onMenuClick}
                                        className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-500 dark:text-neutral-400"
                                    >
                                        <span className="w-6 h-6" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_df5a8182.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_df5a8182.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                    </button>
                                )}
                                <div className="flex flex-col">
                                    <h2 className="text-xl font-bold text-text-primary dark:text-neutral-100 tracking-tight">
                                        {t('navigation.moneyJournal', 'Money Journal')}
                                    </h2>
                                    <p className="text-xs text-neutral-500 dark:text-neutral-400 hidden lg:block">
                                        {t('journals.emptyDescription', 'Journals are global journals of financial operations.')}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                {activeTab === 'my' && (
                                    <button
                                        id="onboarding-add-journal"
                                        onClick={handleCreateJournal}
                                        className="flex-shrink-0 h-8 px-4 text-xs font-bold bg-accent-primary text-white rounded-lg hover:bg-accent-primary/90 transition-all shadow-sm active:scale-95 flex items-center gap-2"
                                    >
                                        <span className="w-3.5 h-3.5" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_journal.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_journal.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                                        <span>{t('boardJournals.addJournal') || 'Add Journal'}</span>
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* List Content */}
                        <div className="flex-1 relative min-h-0">
                            {(currentLoading && currentJournals.length === 0) || !isUrlSynced ? (
                                <div className="flex items-center justify-center h-full">
                                    <SectionLoader size="sm" />
                                </div>
                            ) : (
                                <AnimatePresence mode="wait" initial={false}>
                                    <motion.div
                                        key={`list-${activeTab}`}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        transition={{ duration: 0.2, ease: "easeInOut" }}
                                        className="w-full h-full relative"
                                    >
                                        <BoardJournalList
                                            boardId=""
                                            standalone
                                            journals={currentJournals as any}
                                            onSelectJournal={handleJournalSelect}
                                            onAddJournal={activeTab === 'my' ? handleCreateJournal : undefined}
                                            onDeleteJournal={activeTab === 'my' ? deleteJournal : undefined}
                                            onShareJournal={activeTab === 'my' ? handleShareJournal : undefined}
                                            onRemoveSharedAccess={activeTab === 'shared' ? removeSharedAccess : undefined}
                                            isSharedView={activeTab === 'shared'}
                                            hideHeaderAction={true}
                                        />
                                    </motion.div>
                                </AnimatePresence>
                            )}
                        </div>

                        {/* Bottom Tabs - Moved from top header */}
                        <div className="shrink-0 pt-4 pb-2 flex justify-center">
                            <div className="flex items-center gap-1 bg-neutral-100/50 dark:bg-neutral-800/50 p-1 rounded-xl backdrop-blur-sm ring-1 ring-neutral-200/50 dark:ring-neutral-700/50">
                                <button
                                    onClick={() => handleTabChange('my')}
                                    className={`
                                        px-4 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-2
                                        ${activeTab === 'my'
                                            ? 'bg-white dark:bg-neutral-900 text-accent-primary shadow-sm ring-1 ring-neutral-200/50 dark:ring-neutral-800'
                                            : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200'
                                        }
                                    `}
                                >
                                    {t('journals.myJournals', 'My Journals')}
                                    {journals.length > 0 && (
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeTab === 'my' ? 'bg-accent-primary/10 dark:bg-accent-primary/20 text-accent-primary' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400'
                                            }`}>
                                            {journals.length}
                                        </span>
                                    )}
                                </button>
                                <button
                                    onClick={() => handleTabChange('shared')}
                                    className={`
                                        px-4 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-2
                                        ${activeTab === 'shared'
                                            ? 'bg-white dark:bg-neutral-900 text-accent-primary shadow-sm ring-1 ring-neutral-200/50 dark:ring-neutral-800'
                                            : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200'
                                        }
                                    `}
                                >
                                    {t('journals.sharedWithMe', 'Shared with Me')}
                                    {sharedJournals.length > 0 && (
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeTab === 'shared' ? 'bg-accent-primary/10 dark:bg-accent-primary/20 text-accent-primary' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400'
                                            }`}>
                                            {sharedJournals.length}
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Confirmation Modal */}
            <EditJournalNameModal
                isOpen={!!renamingJournal}
                onClose={() => setRenamingJournal(null)}
                journalId={renamingJournal?.id || ''}
                currentName={renamingJournal?.name || ''}
            />

            <ShareJournalModal
                isOpen={shareModalOpen}
                onClose={() => {
                    setShareModalOpen(false);
                    setJournalToShare(null);
                }}
                journal={journalToShare}
            />

            <UpgradeModal
                isOpen={upgradeModal.isOpen}
                onClose={() => setUpgradeModal(prev => ({ ...prev, isOpen: false }))}
                featureKey={upgradeModal.featureKey}
                currentUsage={upgradeModal.currentUsage}
            />
        </div>
    );
};

export default MoneyJournalView;

